import BaseService from '@/services/BaseService';
const REPORTING_OBLIGATIONS_DASHBOARD_PATH = 'reporting-obligations/dashboard';
const REPORTING_OBLIGATIONS_DATES_PATH = 'reporting-obligations/dates';

export type ReportingObligationDerivedStatus = 'Fulfilled' | 'Overdue' | 'Not Due Yet';

export type ReportingObligationRow = {
  asOfDate: string;
  obligationId: string;
  iqeqId: string;
  borrowerName: string;
  obligationType: string;
  obligationName: string;
  obligationFrequency: string;
  obligationDueDate: string;
  expectedReceiveDate: string;
  offsetDays: number | null;
  fulfillmentStatus: string;
  derivedStatus: ReportingObligationDerivedStatus;
  daysLate: number;
  isUpcoming30Days: boolean;
  supportingDocument: string;
  legalDocument: string;
  pmlConduitCoverage: string;
};

export type ReportingObligationsKpis = {
  facilitiesTracked: number;
  obligationsOverdue: number;
  overdueFacilities: number;
  fulfilledClosedPeriods: number;
  dueObligations: number;
  fulfillmentRate: number;
  dueNext30Days: number;
};

export type ReportingObligationsDashboardResponse = {
  asOfDate: string;
  kpis: ReportingObligationsKpis;
  records: ReportingObligationRow[];
};

function throwIfResponseError(response: Response): never {
  const detail = response.statusText ? `${response.status} ${response.statusText}` : String(response.status);
  throw new Error(`Reporting Obligations request failed (${detail})`);
}

function stringArrayFromBody(body: Record<string, unknown>, ...keys: string[]): string[] {
  for (const key of keys) {
    const raw = body[key];
    if (Array.isArray(raw)) {
      return raw.filter((value): value is string => typeof value === 'string');
    }
  }
  return [];
}

export class ReportingObligationsService extends BaseService {
  constructor() {
    super('/api/digitalconnect-service');
  }

  fetchReportingObligationsDashboard = async (date?: string) => {
    const raw = await this.get<ReportingObligationsDashboardResponse | Response>({
      path: REPORTING_OBLIGATIONS_DASHBOARD_PATH,
      params: date ? { date } : undefined,
    } as never);
    if (raw instanceof Response) {
      throwIfResponseError(raw);
    }
    return raw;
  };

  fetchReportingObligationsDates = async () => {
    const raw = await this.get<unknown>({ path: REPORTING_OBLIGATIONS_DATES_PATH } as never);
    if (raw instanceof Response) {
      throwIfResponseError(raw);
    }
    if (raw && typeof raw === 'object' && !(raw instanceof Response)) {
      return stringArrayFromBody(raw as Record<string, unknown>, 'dates', 'Dates');
    }
    return [];
  };
}
